import { ref } from 'vue';
import { useApiClient } from './useApiClient';
import { logger } from '../utils/logger';

// ===== Types =====

export interface FilePreview {
  file: File;
  name: string;
  size: number;
  type: string;
  preview: string;
  objectUrl: string;
  tempId: string;
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

export interface UploadOptions {
  onProgress?: (percent: number) => void;
  onComplete?: () => void;
  onError?: (error: Error) => void;
  text?: string;
}

export interface UploadResult {
  success: boolean;
  fileId?: string;
  url?: string;
  thumbnail?: string;
  messageId?: number | null;
  message?: string;
  filePreviews?: FilePreview[];
  needRefresh?: boolean;
  error?: string;
}

export function useFileUpload() {
  const isUploading = ref(false);
  const uploadProgress = ref<UploadProgress>({ loaded: 0, total: 0, percentage: 0 });
  const error = ref<string | null>(null);

  const validateFiles = (files: File[]): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    const MAX_FILES = 5;
    const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

    if (files.length > MAX_FILES) {
      errors.push(`Maximum ${MAX_FILES} files allowed`);
    }

    files.forEach((file, index) => {
      if (!ALLOWED_TYPES.includes(file.type)) {
        errors.push(`File ${index + 1}: Unsupported file type ${file.type}`);
      }
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`File ${index + 1}: File size exceeds ${MAX_FILE_SIZE / 1024 / 1024}MB`);
      }
    });

    return { valid: errors.length === 0, errors };
  };

  const createPreview = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!file.type.startsWith('image/')) {
        resolve('');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        resolve(e.target?.result as string);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const uploadFiles = async (
    files: File[], 
    contactId: string | number,
    onProgress?: (progress: UploadProgress) => void
  ): Promise<UploadResult[]> => {
    isUploading.value = true;
    error.value = null;
    uploadProgress.value = { loaded: 0, total: 0, percentage: 0 };

    const validation = validateFiles(files);
    if (!validation.valid) {
      error.value = validation.errors.join(', ');
      isUploading.value = false;
      return [];
    }

    const results: UploadResult[] = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.set('contactId', String(contactId));
        formData.append('attachments[0]', file);

        const result = await new Promise<UploadResult>((resolve) => {
          const xhr = new XMLHttpRequest();

          // Upload progress
          xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
              const progress = {
                loaded: e.loaded,
                total: e.total,
                percentage: Math.round((e.loaded / e.total) * 100)
              };
              uploadProgress.value = progress;
              onProgress?.(progress);
            }
          });

          xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                const response = JSON.parse(xhr.responseText);
                resolve({
                  success: true,
                  fileId: response.fileId || response.id,
                  url: response.url,
                  thumbnail: response.thumbnail || response.preview_url
                });
              } catch (e) {
                resolve({ success: false, error: 'Invalid server response' });
              }
            } else {
              resolve({ 
                success: false, 
                error: `Upload failed: ${xhr.status} ${xhr.statusText}` 
              });
            }
          });

          xhr.addEventListener('error', () => {
            resolve({ success: false, error: 'Network error during upload' });
          });

          xhr.open('POST', '/messages/create');
          xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
          xhr.send(formData);
        });

        results.push(result);

        if (!result.success) {
          error.value = result.error || 'Upload failed';
          break;
        }
      }
    } catch (e) {
      error.value = 'Upload failed due to an error';
      console.error('Upload error:', e);
    } finally {
      isUploading.value = false;
    }

    return results;
  };

  const uploadImages = async (
    files: File[],
    contactId: string | number,
    tempId: string,
    options?: UploadOptions
  ): Promise<UploadResult> => {
    const api = useApiClient();
    const url = api.buildUrl('messages/upload-images');
    const csrfToken = api.getCsrfToken();

    logger.log('FileUpload: Uploading images for contact', contactId, 'with tempId', tempId);

    const form = new FormData();
    form.set('contactId', String(contactId));
    form.set('client_message_id', tempId);
    // Текст сопроводительного сообщения: без него сервер создаёт медиа-сообщение
    // с пустым text и после перезагрузки страницы текст пропадает
    if (options?.text) {
      form.set('message', options.text);
    }
    files.forEach((file, idx) => form.append(`files[${idx}]`, file));
    if (csrfToken) form.set('_csrf', csrfToken);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);
      xhr.withCredentials = true;

      if (xhr.upload && options?.onProgress) {
        // Начинаем с 0%
        logger.log('FileUpload: Starting upload, calling onProgress(0)');
        options.onProgress(0);
        
        xhr.upload.onprogress = (e) => {
          if (!e.lengthComputable) {
            // Даже если length не известен, показываем что загрузка идёт
            logger.log('FileUpload: Progress not computable, calling onProgress(-1)');
            options.onProgress?.(-1);
            return;
          }
          const percent = Math.max(0, Math.min(100, Math.round((e.loaded / e.total) * 100)));
          logger.log('FileUpload: Progress', percent + '%', e.loaded, '/', e.total);
          options.onProgress?.(percent);
        };
      }

      xhr.onerror = () => {
        const error = new Error('Upload failed: network error');
        options?.onError?.(error);
        reject(error);
      };

      xhr.onabort = () => {
        const error = new Error('Upload aborted');
        options?.onError?.(error);
        reject(error);
      };

      xhr.onload = () => {
        try {
          if (xhr.status < 200 || xhr.status >= 300) {
            const error = new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`);
            options?.onError?.(error);
            reject(error);
            return;
          }

          const result = JSON.parse(xhr.responseText || '{}');
          logger.log('FileUpload: Upload response:', result);

          if (!result.success) {
            const error = new Error(result.message || 'Upload failed');
            options?.onError?.(error);
            reject(error);
            return;
          }

          options?.onComplete?.();
          resolve({
            success: true,
            messageId: result.messageId != null ? Number(result.messageId) : null,
            message: result.message,
          });
        } catch (e) {
          const error = new Error('Upload failed: invalid server response');
          options?.onError?.(error);
          reject(error);
        }
      };

      xhr.send(form);
    });
  };

  /**
   * Создать превью для файлов перед загрузкой
   */
  const createFilePreviews = async (files: File[]): Promise<FilePreview[]> => {
    return Promise.all(
      files.map(async (file) => {
        const preview = await createPreview(file);
        return {
          file,
          name: file.name,
          size: file.size,
          type: file.type,
          preview: preview || '',
          objectUrl: URL.createObjectURL(file),
          tempId: `file_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
        };
      })
    );
  };

  /**
   * Отправить сообщение с медиа (двухэтапный процесс)
   */
  const sendWithMedia = async (
    files: File[],
    contactId: string | number,
    tempId: string,
    text?: string,
    options?: UploadOptions
  ): Promise<UploadResult & { filePreviews: FilePreview[] }> => {
    // Создаем превью для оптимистичного отображения
    const filePreviews = await createFilePreviews(files);

    try {
      // Загружаем файлы
      const uploadResult = await uploadImages(files, contactId, tempId, {
        onProgress: options?.onProgress,
        onComplete: options?.onComplete,
        onError: options?.onError,
        text,
      });

      const messageId = uploadResult.messageId;
      const needRefresh = !(messageId && Number.isFinite(messageId) && messageId > 0);

      return {
        ...uploadResult,
        filePreviews,
        needRefresh,
      };
    } catch (error) {
      // Cleanup object URLs on error
      filePreviews.forEach((preview) => {
        if (preview.objectUrl) {
          URL.revokeObjectURL(preview.objectUrl);
        }
      });
      throw error;
    }
  };

  const reset = () => {
    isUploading.value = false;
    uploadProgress.value = { loaded: 0, total: 0, percentage: 0 };
    error.value = null;
  };

  return {
    isUploading,
    uploadProgress,
    error,
    validateFiles,
    createPreview,
    createFilePreviews,
    uploadFiles,
    uploadImages,
    sendWithMedia,
    reset,
  };
}
